import { useState } from 'react';
import { backend } from '../api/backend';
import { useProject } from '../state/project';
import './PublishButton.css';

/**
 * Editor-header control: mint / copy / revoke the read-write collaboration
 * link. Sibling of PublishButton and reuses its styles — but note the two grant
 * very different things, so this one warns explicitly that the link is
 * write access, not viewing.
 */
export default function CollabButton() {
  const project = useProject((s) => s.project);
  const setCollab = useProject((s) => s.setCollab);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!project) return null;

  const shareUrl = project.collab_token
    ? `${window.location.origin}/collab/${project.collab_token}`
    : '';

  async function enable() {
    if (!project) return;
    setBusy(true);
    setError(null);
    try {
      const r = await backend.projects.enableCollab(project.id);
      setCollab(r.collab_enabled, r.collab_token);
      setOpen(true);
    } catch {
      setError('Could not create a collaboration link.');
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!project) return;
    setBusy(true);
    setError(null);
    try {
      const r = await backend.projects.disableCollab(project.id);
      setCollab(r.collab_enabled, r.collab_token);
      setOpen(false);
    } catch {
      setError('Could not revoke the link.');
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — user can select manually */
    }
  }

  return (
    <div className="publish">
      <button
        className={project.collab_enabled ? 'active' : ''}
        disabled={busy}
        onClick={() => (project.collab_enabled ? setOpen((v) => !v) : void enable())}
      >
        {project.collab_enabled ? '● Collaborating' : 'Collaborate'}
      </button>

      {open && project.collab_enabled && (
        <div className="publish-pop" onClick={(e) => e.stopPropagation()}>
          <p className="publish-label">
            <strong>Anyone with this link can edit</strong> — including people who are not
            signed in. Share it only with collaborators.
          </p>
          <div className="publish-row">
            <input
              className="publish-url"
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
            />
            <button className="ghost" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="publish-actions">
            {/* Revoking keeps the token, so re-enabling later yields this same
                URL — but joiners are refused the moment it is off. */}
            <span className="publish-label">Revoking disconnects anyone using it.</span>
            <button className="danger ghost" disabled={busy} onClick={() => void disable()}>
              Revoke link
            </button>
          </div>
          {error && <p className="publish-error">{error}</p>}
        </div>
      )}
      {error && !open && <span className="publish-error">{error}</span>}
    </div>
  );
}
